import {recordings} from "../../proto/compiled";
import {Frame} from "../common/Frame";
import {LoadingOverlay} from "../common/LoadingOverlay";
import styled from "@emotion/styled";
import {Chip, Typography} from "@mui/material";
import {DateUtils} from "../../utils/DateUtils";
import {D} from "../../Constants";
import {theme} from "../../Theme";
import useWave from "use-wave";
import {School, Person, Visibility} from "@mui/icons-material";

interface Props {
  recording?: recordings.IRecording;
  style?: React.CSSProperties;
  onClick?: () => void;
}

export const RecordingCard = (props: Props) => {
  const wave = useWave();
  if (!props.recording) {
    return <Container style={props.style} clip>
      <LoadingOverlay visible={true}/>
    </Container>;
  }
  return <Container
    ref={wave}
    clickable={!!props.onClick}
    style={props.style}
    border={1}
    padding={1}
    clip
    background={theme.palette.background.paper}
    gap={2}
    onClick={props.onClick}
  >
    <img
      src={props.recording.thumbnailUrl!}
      style={{aspectRatio: 1.5, margin: D.unit(-1), objectFit: 'cover'}}
      alt={'Recording Thumbnail'}
    />
    <Frame style={{flex: 1}}>
      <Typography variant="h6">{props.recording.title}</Typography>
      <Typography variant="body2" color={'textSecondary'}>
        {DateUtils.toString(props.recording.dateCreated)}
      </Typography>
      <Typography variant="caption" style={{marginTop: 8}}>
        {props.recording.description}
      </Typography>
      {!!props.recording.participants?.length && (
        <Frame row wrap gap={0.5} marginTop={1}>
          {orderParticipants(props.recording.participants).map(participant => (
            <Chip
              key={participant.id ?? participant.name}
              size="small"
              icon={getParticipantIcon(participant.role)}
              label={participant.name}
            />
          ))}
        </Frame>
      )}
    </Frame>
  </Container>;
};

const Container = styled(Frame)`
    overflow: hidden;
`;

const getParticipantIcon = (role?: recordings.RecordingParticipantRole | null) => {
  switch (role) {
  case recordings.RecordingParticipantRole.rpr_instructor:
    return <School fontSize="small"/>;
  case recordings.RecordingParticipantRole.rpr_student:
    return <Person fontSize="small"/>;
  case recordings.RecordingParticipantRole.rpr_supervisor:
    return <Visibility fontSize="small"/>;
  default:
    return undefined;
  }
};

const orderParticipants = (participants: recordings.IRecordingParticipant[]) => {
  const weight = (role?: recordings.RecordingParticipantRole | null) => {
    switch (role) {
    case recordings.RecordingParticipantRole.rpr_instructor:
      return 0;
    case recordings.RecordingParticipantRole.rpr_student:
      return 1;
    case recordings.RecordingParticipantRole.rpr_supervisor:
      return 2;
    default:
      return 3;
    }
  };

  return [...participants].sort((a, b) => weight(a.role) - weight(b.role));
};
